import { ArrowUpRight, Award, FlaskConical, GraduationCap, HeartHandshake } from 'lucide-react'
import { certificates, journey, type JourneyItem } from '@/data/site'
import { SectionHeading } from '@/components/ui/section-heading'
import { Reveal } from '@/components/ui/reveal'
import { SpotlightCard } from '@/components/ui/spotlight-card'

const kindMeta: Record<JourneyItem['kind'], { icon: typeof GraduationCap; label: string; color: string }> = {
  education: { icon: GraduationCap, label: 'Education', color: 'text-sky' },
  research: { icon: FlaskConical, label: 'Research', color: 'text-lilac' },
  volunteer: { icon: HeartHandshake, label: 'Volunteering', color: 'text-blush' },
}

export function Journey() {
  return (
    <section id="journey" aria-labelledby="journey-title" className="relative py-24 md:py-32">
      <div className="container">
        <SectionHeading
          id="journey-title"
          eyebrow="Journey"
          title="Education, experience & credentials."
          description="Two degrees in parallel, a research internship at IIT Madras, and time spent giving back."
        />

        <div className="grid gap-16 lg:grid-cols-[1.3fr_1fr]">
          {/* Timeline */}
          <ol className="relative space-y-4 before:absolute before:bottom-4 before:left-[19px] before:top-4 before:w-px before:bg-gradient-to-b before:from-white/15 before:via-white/10 before:to-transparent">
            {journey.map((item, idx) => {
              const meta = kindMeta[item.kind]
              const Icon = meta.icon
              return (
                <Reveal as="li" key={item.title + item.org} delay={idx * 0.06} className="relative pl-14">
                  <span className="absolute left-0 top-6 grid h-10 w-10 place-items-center rounded-full border border-white/10 bg-ink-800">
                    <Icon className={`h-4 w-4 ${meta.color}`} />
                  </span>
                  <SpotlightCard className="p-6">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className={`font-mono text-[11px] uppercase tracking-[0.18em] ${meta.color}`}>{meta.label}</p>
                      {item.period && <p className="font-mono text-xs text-zinc-500">{item.period}</p>}
                    </div>
                    <h3 className="mt-2 text-lg font-medium text-white">{item.title}</h3>
                    <p className="text-sm text-zinc-400">{item.org}</p>
                    <p className="mt-3 text-sm leading-relaxed text-zinc-400">{item.description}</p>
                    {item.points && (
                      <ul className="mt-3 space-y-1.5">
                        {item.points.map((p) => (
                          <li key={p} className="flex gap-2.5 text-sm text-zinc-400">
                            <span className="mt-[7px] h-1 w-1 shrink-0 rounded-full bg-zinc-500" aria-hidden="true" />
                            {p}
                          </li>
                        ))}
                      </ul>
                    )}
                  </SpotlightCard>
                </Reveal>
              )
            })}
          </ol>

          {/* Certifications */}
          <div>
            <Reveal>
              <h3 className="eyebrow mb-5 flex items-center gap-2">
                <Award className="h-3.5 w-3.5 text-lilac" /> Certifications
              </h3>
            </Reveal>
            <ul className="space-y-2">
              {certificates.map((c, idx) => (
                <Reveal as="li" key={c.title} delay={idx * 0.05}>
                  <a
                    href={c.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group flex items-center justify-between gap-4 rounded-2xl border border-white/[0.06] bg-white/[0.02] px-5 py-4 transition hover:border-white/[0.14] hover:bg-white/[0.04]"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[15px] text-zinc-100">{c.title}</span>
                      <span className="text-xs text-zinc-500">
                        {c.issuer} · {c.year}
                      </span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 shrink-0 text-zinc-500 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-zinc-200" />
                    <span className="sr-only">(view certificate, opens in new tab)</span>
                  </a>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  )
}
